import React, { useEffect, useState, useRef } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import serverURL from '../config';
import toast from 'react-hot-toast';
import ConfirmationModal from './ConfirmationModal';
import ModalShell from './ModalShell';

const EventModal = ({ title, event, closeEventModal, fetchEvents }) => {
    const [formData, setFormData] = useState({});
    const dateInputRef = useRef(null);

    // Helper function to format date for datetime-local input
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Convert to local timezone and format as YYYY-MM-DDTHH:mm
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    useEffect(() => {
        setFormData({
            name: event?.name || '',
            institution: event?.institution || '',
            event_date: formatDateForInput(event?.event_date) || '',
            candidate_count: event?.candidate_count || '',
            passkey: event?.passkey || '',
            duration: event?.duration || '',
            difficulty_level: event?.difficulty_level || '',
            pass_percentage: event?.pass_percentage || '',
        });
    }, [event]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'candidate_count' && value !== '') {
            const num = parseInt(value, 10);
            if (num < 0) {
                setFormData((prev) => ({ ...prev, [name]: 0 }));
                return;
            }
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleClose = () => {
        setFormData({});
        closeEventModal();
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (title !== "Delete Event") {
            if (!formData.name || !formData.event_date || !formData.passkey || !formData.institution) {
                toast.error('Please fill in all required fields.');
                return;
            }
        }

        try {
            const headers = { 'Content-Type': 'application/json' };

            if (title === "Add Event") {
                await axios.post(`${serverURL}/events`, formData, { headers });
                toast.success('Event created successfully!');
            } else if (title === "Edit Event") {
                await axios.put(`${serverURL}/events/${event.id}`, formData, { headers });
                toast.success('Event edited successfully!');
            } else if (title === "Delete Event") {
                await axios.delete(`${serverURL}/events/${event.id}`, { headers });
                toast.success('Event deleted successfully!');
            }
            if (fetchEvents) {
                fetchEvents();
            }
            handleClose();
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred.';
            toast.error(errorMessage);
            console.error('Error details:', error.response || error.message);
        }
    };

    // For delete, show ConfirmationModal instead of the form modal
    if (title === 'Delete Event') {
        return (
            <ConfirmationModal
                isOpen={true}
                title="Delete Event"
                message="Are you sure you want to delete this event?"
                onConfirm={handleSubmit}
                onCancel={handleClose}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-500 hover:bg-red-600"
            />
        );
    }

    return (
        <ModalShell title={title} onClose={handleClose}>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label htmlFor="event-name" className="block text-gray-700 text-sm font-bold mb-2">
                        Event Name
                    </label>
                    <input
                        id="event-name"
                        type="text"
                        name="name"
                        placeholder="Enter event name"
                        value={formData.name || ''}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded ${!formData.name && 'border-red-500'}`}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="event-institution" className="block text-gray-700 text-sm font-bold mb-2">
                        Institution Name
                    </label>
                    <input
                        id="event-institution"
                        type="text"
                        name="institution"
                        placeholder="Enter institution name"
                        value={formData.institution || ''}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded ${!formData.institution && 'border-red-500'}`}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="event-date" className="block text-gray-700 text-sm font-bold mb-2">
                        Event Date
                    </label>
                    <input
                        id="event-date"
                        ref={dateInputRef}
                        type="datetime-local"
                        name="event_date"
                        value={formData.event_date || ''}
                        onChange={handleChange}
                        onClick={(e) => {
                            // Ensure the picker opens when clicking anywhere in the input
                            e.target.showPicker?.();
                        }}
                        className={`w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 cursor-pointer ${!formData.event_date && 'border-red-500'}`}
                    />
                </div>

                <div className="mb-4">
                    <label htmlFor="event-candidate-count" className="block text-gray-700 text-sm font-bold mb-2">
                        Candidate Count
                    </label>
                    <input
                        id="event-candidate-count"
                        type="number"
                        name="candidate_count"
                        placeholder="Enter candidate count"
                        value={formData.candidate_count || ''}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                            }
                        }}
                        min={0}
                        className={`w-full p-2 border rounded ${!formData.candidate_count && 'border-red-500'}`}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="event-passkey" className="block text-gray-700 text-sm font-bold mb-2">
                        Passkey
                    </label>
                    <input
                        id="event-passkey"
                        type="text"
                        name="passkey"
                        placeholder="Enter event passkey"
                        value={formData.passkey || ''}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded ${!formData.passkey && 'border-red-500'}`}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="event-duration" className="block text-gray-700 text-sm font-bold mb-2">
                        Duration (minutes)
                    </label>
                    <input
                        id="event-duration"
                        type="number"
                        name="duration"
                        placeholder="Enter duration"
                        value={formData.duration || ''}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded ${!formData.duration && 'border-red-500'}`}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="event-difficulty" className="block text-gray-700 text-sm font-bold mb-2">
                        Difficulty Level
                    </label>
                    <select
                        id="event-difficulty"
                        name="difficulty_level"
                        value={formData.difficulty_level || ''}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded ${!formData.difficulty_level && 'border-red-500'}`}
                    >
                        <option value="" disabled>Select Difficulty Level</option>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                    </select>
                </div>
                <div className="mb-4">
                    <label htmlFor="event-pass-percentage" className="block text-gray-700 text-sm font-bold mb-2">
                        Pass Percentage
                    </label>
                    <input
                        id="event-pass-percentage"
                        type="number"
                        name="pass_percentage"
                        placeholder="Enter pass percentage"
                        min={23}
                        value={formData.pass_percentage || ''}
                        onChange={handleChange}
                        className={`w-full p-2 border rounded ${!formData.pass_percentage && 'border-red-500'}`}
                    />
                </div>
                <div className="flex justify-end">
                    <button
                        type="submit"
                        className="bg-red-500 text-white px-4 py-2 rounded"
                    >
                        Save
                    </button>
                </div>
            </form>
        </ModalShell>
    );
};

EventModal.propTypes = {
    title: PropTypes.string.isRequired,
    event: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        institution: PropTypes.string,
        event_date: PropTypes.string,
        candidate_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        passkey: PropTypes.string,
        duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        difficulty_level: PropTypes.string,
        pass_percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    closeEventModal: PropTypes.func.isRequired,
    fetchEvents: PropTypes.func.isRequired,
};

EventModal.defaultProps = {
    event: null,
};

export default EventModal;
